import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';

interface LoadingContextType {
  isLoading: boolean;
  showLoading: () => void;
  hideLoading: () => void;
  setLoading: (loading: boolean) => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

interface LoadingProviderProps {
  children: ReactNode;
}

/**
 * LoadingProvider manages global loading state.
 *
 * Usage examples:
 *
 * 1. Direct usage in components:
 * const { showLoading, hideLoading } = useLoading();
 *
 * const handleSubmit = async () => {
 *   showLoading();
 *   try {
 *     await someAsyncOperation();
 *   } finally {
 *     hideLoading();
 *   }
 * };
 *
 * 2. Using the useHttpWithLoading hook (recommended):
 * const { post } = useHttpWithLoading();
 * const data = await post('/api/endpoint', payload);
 * // Loading is automatically handled!
 */
export const LoadingProvider: React.FC<LoadingProviderProps> = ({
  children,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const showLoading = useCallback(() => {
    setIsLoading(true);
  }, []);

  const hideLoading = useCallback(() => {
    // Add a small delay to prevent flickering for fast requests
    setTimeout(() => {
      setIsLoading(false);
    }, 100);
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setIsLoading(loading);
  }, []);

  const value: LoadingContextType = {
    isLoading,
    showLoading,
    hideLoading,
    setLoading,
  };

  return (
    <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>
  );
};
